/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import { DATE, formatDateUTC, toTimestamp } from '@utils/date';
import { Sponsorship, SponsoredInitiative } from '@g17eco/types/sponsorship';
import { Subscription, SubscriptionStatus } from '@g17eco/types/subscriptions';

export interface SponsoredCompanyRow {
  _id: string;
  name: string;
  customerId: string;
  canBeSponsored: boolean;
  subscriptionId: string;
  status: Subscription['status'];
  startDate: number;
  endDate?: number;
  cancelDate?: number;
  periodStart: number;
  periodEnd: number;
  discountStart?: number;
  discountEnd?: number;
  subscriptions: Subscription[];
  sponsorship?: Sponsorship;

  /**
   * When is renewal due?
   * Legacy should be based on periodEnd, on new sponsorship it should be based on sponsorship.periodEndDate
   */
  renewalDate?: number;
}

const getEarliestTs = (unixTsArray: (number | null | undefined)[]): number | undefined => {
  return unixTsArray.filter((d) => typeof d === 'number').sort((a, b) => a - b)[0];
}

const hydrator = (sub: Subscription, sponsorship: Sponsorship | undefined) => {

  // Closest date when either it will be canceled, ended or discount will end
  // @TODO: might just display discount end date?
  const discountEnd = getEarliestTs([sub.cancelDate, sub.endDate, sub.discount?.end]);
  const renewalDate = sponsorship?.periodEndDate ?
    toTimestamp(sponsorship.periodEndDate) :
    getEarliestTs([sub.cancelDate, sub.endDate]);

  return {
    status: sub.status,
    subscriptionId: sub.id,
    startDate: sub.startDate,
    endDate: sub.endDate,
    cancelDate: sub.cancelDate,
    /** Start of the current period that the subscription has been invoiced for. */
    periodStart: sub.periodStart,
    /** End of the current period that the subscription has been invoiced for. At the end of this period, a new invoice will be created. **/
    periodEnd: sub.periodEnd,
    discountStart: sub.discount?.start,
    discountEnd,

    renewalDate,
  }
}

export const convertToTableRows = (data: SponsoredInitiative[]): SponsoredCompanyRow[] => {
  const company = new Map<string, SponsoredCompanyRow>();

  data.forEach((c) => {
    const current = company.get(c._id);
    if (!current) {
      company.set(c._id, {
        _id: c._id,
        name: c.name,
        customerId: c.customerId,
        canBeSponsored: c.canBeSponsored,
        ...hydrator(c.subscription, c.sponsorship),
        subscriptions: [c.subscription],
        sponsorship: c.sponsorship,
      });
      return;
    }

    current.subscriptions.push(c.subscription);
    if (isLatest(current, c.subscription)) {
      company.set(c._id, {
        ...current,
        ...hydrator(c.subscription, c.sponsorship),
      });
    }
  });

  return Array.from(company.values());
};

const activeStatus: SubscriptionStatus[] = ['active', 'trialing'];

export const isSponsorshipActive = (company: SponsoredCompanyRow): boolean => {
  return activeStatus.includes(company.status);
}

const isLatest = (company: SponsoredCompanyRow, subscription: SponsoredInitiative['subscription']): boolean => {
  const currentActive = activeStatus.includes(company.status);
  const incomingActive = activeStatus.includes(subscription.status);

  if (currentActive && !incomingActive) {
    // Always prioritize active sub
    return false;
  }
  if (incomingActive && !currentActive) {
    // Always prioritize active sub
    return true;
  }

  return company.periodEnd < subscription.periodEnd;
}

export const fmtDate = (date?: number | null, format = DATE.DEFAULT_DASHES) => date ? formatDateUTC(new Date(date * 1000), format) : '-';

export const pcComplete = (start: number, end: number, now: number): number => {
  if (now > end) {
    return 100;
  }
  if (now < start) {
    return 0;
  }
  return Math.round(1000 * (now - start) / (end - start)) / 10;
}

export const getSubscriptionStartDate = (sub: Subscription): number => {
  return sub.startDate;
}

export const getSubscriptionEndDate = (sub: Subscription): number => {
  return sub.cancelDate ?? sub.endDate ?? sub.periodEnd;
}
